import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const RecentActivityFeed = ({ activities = [], onViewAll = () => {} }) => {
  const navigate = useNavigate();

  const getActivityIcon = (type) => {
    switch (type) {
      case 'birthday_sent':
        return { name: 'Gift', color: 'text-success' };
      case 'message_sent':
        return { name: 'Send', color: 'text-primary' };
      case 'contact_added':
        return { name: 'UserPlus', color: 'text-accent' };
      case 'contact_updated':
        return { name: 'Edit', color: 'text-secondary' };
      case 'template_created':
        return { name: 'FileText', color: 'text-warning' };
      default:
        return { name: 'Activity', color: 'text-text-secondary' };
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return time?.toLocaleDateString();
  };

  const handleActivityClick = (activity) => {
    switch (activity?.type) {
      case 'contact_added': case'contact_updated': navigate('/contact-details', { state: { contactId: activity?.contactId } });
        break;
      case 'birthday_sent': case'message_sent': navigate('/message-history');
        break;
      case 'template_created': navigate('/message-templates');
        break;
      default:
        break;
    }
  };

  return (
    <div className="bg-surface rounded-lg border border-border shadow-card p-4 lg:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={20} className="text-primary" />
          <h2 className="text-lg font-semibold text-text-primary">Recent Activity</h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onViewAll}
          iconName="ArrowRight"
          iconPosition="right"
        >
          View All
        </Button>
      </div>
      {activities?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Activity" size={48} className="mx-auto mb-3 text-text-secondary opacity-50" />
          <p className="text-text-secondary">No recent activity</p>
          <p className="text-sm text-text-secondary mt-1">
            Start by adding contacts or sending messages
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities?.slice(0, 6)?.map((activity) => {
            const iconConfig = getActivityIcon(activity?.type);
            
            return (
              <div
                key={activity?.id}
                onClick={() => handleActivityClick(activity)}
                className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted transition-micro cursor-pointer"
              >
                <div className={`w-8 h-8 rounded-full bg-background border border-border flex items-center justify-center flex-shrink-0 ${iconConfig?.color}`}>
                  <Icon name={iconConfig?.name} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary">
                        {activity?.title}
                      </p>
                      <p className="text-sm text-text-secondary mt-1">
                        {activity?.description}
                      </p>
                      {activity?.contact && (
                        <div className="flex items-center space-x-2 mt-2">
                          <div className="w-6 h-6 rounded-full overflow-hidden bg-muted">
                            {activity?.contact?.avatar ? (
                              <Image
                                src={activity?.contact?.avatar}
                                alt={activity?.contact?.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-primary/20">
                                <Icon name="User" size={12} className="text-primary" />
                              </div>
                            )}
                          </div>
                          <span className="text-xs text-text-secondary">
                            {activity?.contact?.name}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-text-secondary ml-2 flex-shrink-0">
                      {formatTimeAgo(activity?.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecentActivityFeed;